import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MenuButton } from "@/components/Buttons/MenuButton";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { langDirection } from "@/utils/languages";
import styles from "./css/SideBar.module.css";
import { UserAvatar } from "../addons/UserAvatar";
import { SettingsIcon } from "@/icons/SettingsIcon";
import { truncateText } from "@/utils/truncateText";
import { clearAll } from "@/utils/fetchData";
import { WeitzIcon } from "@/icons/WeitzIcon";
import { CityViewIcon } from "@/icons/cityViewIcon";
import { ProjectsViewIcon } from "@/icons/ProjectsViewIcon";
import { ManagmentViewIcon } from "@/icons/ManagmentViewIcon";
import { OutlinePlansIcon } from "@/icons/OutlinePlans";
import { getEntityName } from "@/utils/getEntityName";

const SideBar: React.FC = () => {
  const { i18n, t: tLanguageSwitcher } = useTranslation("LanguageSwitcher");
  const { t } = useTranslation("SideBar");
  const user = useSelector((state: RootState) => state.user.user);
  const city = useSelector((state: RootState) => state.entity.userCity);
  const ao = useSelector((state: RootState) => state.entity.userAo);
  const entityType = useSelector((state: RootState) => state.entity.entityType);
  const isSuprAdmin = user.user_type === "super_admin";
  const isAdmin = user.user_type === "admin";
  const userAo = useSelector((state: RootState) => state.entity.userAo);
  const [selected, setSelected] = React.useState<string>("Assist");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedLang, setSelectedLang] = useState("English");
  const direction = langDirection(i18n.language);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState("");

  const handleLogoutClick = () => {
    clearAll(dispatch);
    navigate(`/${getEntityName(entityType, city, ao)}/`);
  };

  useEffect(() => {
    const splitted = location.pathname.split("/");
    const path = splitted[2];
    const sub_path =
      splitted.length >= 4 ? location.pathname.split("/")[3] : "";
    switch (path) {
      case "city":
        setSelected("City");
        setSelectedSub("");
        break;
      case "cities":
        setSelected("Cities");
        setSelectedSub("");
        break;
      case "projects":
        setSelected("Projects");
        setSelectedSub("");
        break;
      case "managment":
        // Check if user is admin and viewing city-parameters
        if (user.user_type === "admin" && sub_path === "city-parameters") {
          setSelected("CityManagement");
          setSelectedSub("");
        } else {
          setSelected("Managment");
          if (["cities", "users", "super-admin-users", "city-parameters", "general-parameters"].includes(sub_path)) {
            setSelectedSub(sub_path);
          } else {
            setSelectedSub("");
          }
        }
        break;
      case "outline_plans":
        setSelected("OutlinePlans");
        setSelectedSub("");
        break;
      case "users":
        setSelected("Users");
        setSelectedSub("");
        break;
      default:
        setSelected("");
        setSelectedSub("");
    }
  }, [location]);

  const handleMenuClick = (selectedItem: string) => {
    const navigateTo = (path: string) => {
      setSelected(selectedItem);
      navigate(`/${getEntityName(entityType, city, ao)}/${path}`);
    };

    switch (selectedItem) {
      case "City":
        navigateTo("city");
        break;
      case "Cities":
        navigateTo("cities");
        break;
      case "Projects":
        navigateTo("projects");
        break;
      case "Managment":
        setSelected("Managment");
        // navigateTo("managment");
        break;
      case "CityManagement":
        setSelected("CityManagement");
        // For city admin, navigate directly to their city parameters page
        if (user.user_type === "admin" && city?.id) {
          navigate(`/${getEntityName(entityType, city, ao)}/managment/city-parameters/${city.id}`);
        }
        break;
      case "OutlinePlans":
        setSelected("OutlinePlans");
        navigateTo("outline_plans");
        break;
      case "Users":
        setSelected("Users");
        navigateTo("users");
        break;    
      default:
        navigateTo("city");
    }
  };

  const langShortcuts: { [key: string]: string } = {
    [tLanguageSwitcher("English")]: "en",
    [tLanguageSwitcher("Hebrew")]: "he",
  };

  const langShow: { [key: string]: string } = {
    en: "English",
    "en-US": "English",
    he: "Hebrew",
  };

  const handleLang = (value: string | undefined) => {
    if (value) {
      const lang = langShortcuts[value];
      setSelectedLang(value);
      i18n.changeLanguage(lang);
      localStorage.setItem("i18nextLng", lang);
      document.documentElement.dir = langDirection(lang);
    }
  };

  useEffect(() => {
    const langShowRes = tLanguageSwitcher(langShow[i18n.language]);
    if (langShowRes) {
      document.documentElement.dir = langDirection(i18n.language);
      setSelectedLang(langShowRes);
    }
  }, [i18n.language]);

  // const documentsSubOptions = [{display: t("toApprove"), navigateTo: ""}, {display: t("approved"), navigateTo: "approved"}, {display: t("notApproved"), navigateTo: "not_approved"}];
  const entityName = getEntityName(entityType, city, ao);
  const isWeitzSuperAdmin = user.user_type === "super_admin" && entityName?.toLowerCase() === "weitz";
  
  const adminSubOptions = [
    { display: t("cities"), navigateTo: "cities" },
    ...(!isWeitzSuperAdmin ? [{ display: t("users"), navigateTo: "users" }] : []),
    { display: t("allUsers"), navigateTo: "super-admin-users" },
    { display: t("generalParameters"), navigateTo: "general-parameters" },
    { display: t("cityParameters"), navigateTo: "city-parameters" },
  ];
  const langOptions = [
    tLanguageSwitcher("English"),
    tLanguageSwitcher("Hebrew"),
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const settingsIcon = document.getElementById("settingsIcon");
      const userMenu = document.getElementById("userMenu");
      if (
        settingsIcon &&
        !settingsIcon.contains(event.target as Node) &&
        userMenu &&
        !userMenu.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isUserMenuOpen]);

  return (
    <div className={styles.container}>
      <div
        className={`${styles.elements} ${direction === "rtl" ? styles.rtl : ""}`}
      >
        <div className={styles.title}>
          <WeitzIcon />
        </div>
        <div className={styles.options}>
          {user.user_type === "external_municipal" &&
            <MenuButton
            text={t("projectsLook")}
            onClick={handleMenuClick}
            selected={selected}
            id="Cities"
            iconColorType="text"
              iconComponent={ProjectsViewIcon}
            />
          }
          {(user.user_type === "admin" || user.user_type === "internal_municipal") &&
          <>
          <MenuButton
            text={t("city")}
            onClick={handleMenuClick}
            selected={selected}
            id="City"
            iconColorType="text"
            iconComponent={CityViewIcon}
          />
          <MenuButton
            text={t("projects")}
            onClick={handleMenuClick}
            selected={selected}
            id="Projects"
            iconColorType="text"
              iconComponent={ProjectsViewIcon}
            />
          <MenuButton
            text={t("managment")}
            onClick={handleMenuClick}
            selected={selected}
            id="CityManagement"
            iconColorType="text"
            iconComponent={ManagmentViewIcon}
          />
          </>}
          {(user.user_type === "adminn") &&
          <>
          <MenuButton
            text={t("outlinePlans")}
            onClick={handleMenuClick}
            selected={selected}
            id="OutlinePlans"
            iconColorType="text"
            iconComponent={OutlinePlansIcon}
          />
          <MenuButton
            text={t("usersManagment")}
            onClick={handleMenuClick}
            selected={selected}
            id="Users"
            iconColorType="text"
            iconComponent={ManagmentViewIcon}
          />
          </>
          }
          {user.user_type === "super_admin" && 
            <MenuButton
            text={t("managment")}
            onClick={handleMenuClick}
            selected={selected}
            id="Managment"
            iconColorType="text"
            iconComponent={ManagmentViewIcon}
            subOptions={adminSubOptions}
            shouldOpenSub={true}
            selectedSub={selectedSub}
          />}
          {/* )} */}
        </div>
        <div
          className={`${styles.bottom} ${direction === "rtl" ? styles.rtl : ""}`}
        >
          {entityType === "ao" && 
            <div className={styles.aoSection}>
              <div className={styles.aoLogo}>
                {userAo?.logo && <img src={userAo.logo} alt="ao logo" />}
              </div>
              <h1 className={styles.aoName}>{userAo?.name}</h1>
            </div>
          }
          <div className={styles.userSectionWrapper}>
            <div id="userSection" className={styles.userSection}>
              <UserAvatar
                color={user.color || "blue"}
                userImage={user.user_image}
              />
              <div className={styles.userText}>
                <p className={styles.hello}>{t("hello")}</p>
                <p className={styles.userName}>
                  <b>{truncateText(user.user_name, 6)}</b>
                </p>
              </div>
              <div
                id="settingsIcon"
                className={styles.settings}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsUserMenuOpen(!isUserMenuOpen);
                }}
              >
                <SettingsIcon />
              </div>
            </div>
            {isUserMenuOpen && (
              <div id="userMenu" className={styles.userMenu}>
                <div
                  className={styles.menuItem}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    navigate(`/${getEntityName(entityType, city, ao)}/user/settings`);
                    setIsUserMenuOpen(false); // Close menu after navigation
                  }}
                >
                  {t("settings")}
                </div>
                <div
                  className={styles.menuItem}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    handleLogoutClick();
                    setIsUserMenuOpen(false); // Close menu after logout
                  }}
                >
                  {t("logout")}
                </div>
              </div>
            )}
          </div>
          {/* <LangSwithcer/> */}
        </div>
      </div>
    </div>
  );
};

export default SideBar;
